/**
 * Standardized column width constants for consistent table layouts
 * These widths are optimized for content readability and consistent UI
 * Widths aggressively reduced to eliminate horizontal scrolling on Team Lead tables
 * Optimized for 1440px laptop screens with comfortable margins
 */
export const COLUMN_WIDTHS = {
  account: 130, // Account/Brand names (reduced from 140, saves 10px)
  accountTags: 100, // Account tag icons (compact, reduced from 120)
  actions: 80, // Action buttons
  assignee: 130, // User names with designations (reduced from 140, saves 10px)
  brand: 130, // Brand information (secondary, reduced from 140, saves 10px)
  completedDate: 115, // Completion dates (reduced from 130)

  complexity: 100, // Simple labels (Basic/Average/Complex, reduced from 110, saves 10px)
  createdAt: 115, // Date displays (reduced from 130)
  // User columns
  createdBy: 110, // Creator names (reduced from 120, saves 10px)

  designType: 140, // Design type subcategories (reduced from 160)

  // Date columns
  dueDate: 110, // Date displays (reduced from 120)
  // Core columns
  name: 160, // Request names (reduced from 175, saves 15px - uses line-clamp-3 for wrapping)
  queuePosition: 80, // Numbers (very compact)

  // Special columns
  rating: 120,
  // Content columns
  requestType: 145, // Request/Design type labels (reduced from 160, saves 15px)

  // Selection and actions
  select: 50, // Checkbox column
  status: 150, // Status badges (increased from 135 to properly display "Ready for Review" and "Design in Progress")
} as const;

/**
 * Column width presets for common table layouts
 */
export const TABLE_LAYOUT_PRESETS = {
  // Customer tables - focus on request details
  customer: {
    createdAt: COLUMN_WIDTHS.createdAt,
    name: COLUMN_WIDTHS.name,
    requestType: COLUMN_WIDTHS.requestType,
    status: COLUMN_WIDTHS.status,
  },

  // SuperAdmin tables - focus on management data
  superadmin: {
    account: COLUMN_WIDTHS.account,
    accountTags: COLUMN_WIDTHS.accountTags,
    assignee: COLUMN_WIDTHS.assignee,
    brand: COLUMN_WIDTHS.brand,
    completedDate: COLUMN_WIDTHS.completedDate,
    complexity: COLUMN_WIDTHS.complexity,
    createdAt: COLUMN_WIDTHS.createdAt,
    createdBy: COLUMN_WIDTHS.createdBy,
    dueDate: COLUMN_WIDTHS.dueDate,
    name: COLUMN_WIDTHS.name,
    rating: COLUMN_WIDTHS.rating,
    requestType: COLUMN_WIDTHS.requestType,
    status: COLUMN_WIDTHS.status,
  },

  // Team tables - focus on work assignments
  team: {
    accountTags: COLUMN_WIDTHS.accountTags,
    assignee: COLUMN_WIDTHS.assignee,
    complexity: COLUMN_WIDTHS.complexity,
    dueDate: COLUMN_WIDTHS.dueDate,
    name: COLUMN_WIDTHS.name,
    status: COLUMN_WIDTHS.status,
  },
} as const;

export const MOBILE_COLUMN_WIDTHS: Record<string, number> = {
  account: 180,
  accountTags: 120,
  assignee: 200,
  brand: 260,
  completedDate: 130,
  complexity: 130,
  createdAt: 130,
  createdBy: 180,
  customerDueDate: 130,
  designType: 220,
  due: 130,
  dueDate: 130,
  rating: 120,
  requestType: 180,
  status: 210,
};

export type ColumnWidthKey = keyof typeof COLUMN_WIDTHS;
export type TableLayoutPreset = keyof typeof TABLE_LAYOUT_PRESETS;
